import React from 'react';
import Raven from 'raven-js';
import GenericError from "containers/Error/GenericError";
import NetworkError from "containers/Error/NetworkError";
import ForbiddenError from "containers/Error/ForbiddenError";
import {connect} from "react-redux";
import {logout} from 'actions/user';
import {FormattedMessage} from "react-intl";
import debug from './debug';

export const errorCodes = {
    invalid_access_token: 1,
    invalid_password: 2,
    refresh_token_not_found: 3,
    user_does_not_exist: 4,
    no_assignable_public_number: 100,
    owning_operator_not_specified: 101,
    authentication_module_unavailable: 102,
    general_login_failure: 103,
    invalid_service_type: 104,
    operator_not_found: 105,
    operator_already_registered: 106,
    contact_not_found: 107,
    no_contacts_found: 108,
    service_not_found: 109,
    no_services_found: 110,
    unauthorized_access: 111,
    create_ad_failure: 112,
    update_ad_failure: 113,
    service_no_public_number: 114,
    operator_module_unavailable: 115,
    customer_module_unavailable: 116,
    operator_not_approved: 117,
    text_message_too_long: 118,
    customer_profile_not_found: 119,
    customer_profile_note_not_found: 120,
    service_pricelist_not_found: 121,
    invalid_time_interval: 122,
    duplicate_time_interval: 123,
    customer_not_found: 124,
    service_no_public_numbers_assigned: 125,
    invalid_value: 126,
    operator_login_not_unique: 400,
    operator_phone_number_not_unique: 401
};

export const errorMapping = {
    [errorCodes.invalid_access_token]:
        <FormattedMessage id="login.error.invalid_access_token" defaultMessage="Invalid access token"/>,
    [errorCodes.invalid_password]:
        <FormattedMessage id="login.error.invalid_password" defaultMessage="Invalid password"/>,
    [errorCodes.refresh_token_not_found]:
        <FormattedMessage id="login.error.invalid_refresh_token" defaultMessage="Invalid refresh token"/>,
    [errorCodes.user_does_not_exist]:
        <FormattedMessage id="login.error.user_not_found" defaultMessage="User does not exist"/>,
    [errorCodes.no_assignable_public_number]:
        <FormattedMessage id="error.no_assignable_number" defaultMessage="There is no assignable public number"/>,
    [errorCodes.owning_operator_not_specified]:
        '',
    [errorCodes.authentication_module_unavailable]:
        '',
    [errorCodes.general_login_failure]:
        '',
    [errorCodes.invalid_service_type]:
        <FormattedMessage id="error.invalid_service_type" defaultMessage="Unknown service type"/>,
    [errorCodes.operator_not_found]:
        <FormattedMessage id="error.operator_not_found" defaultMessage="Operator does not exist"/>,
    [errorCodes.operator_already_registered]:
        <FormattedMessage id="error.operator_already_registered" defaultMessage="Operator is already registered"/>,
    [errorCodes.contact_not_found]:
        <FormattedMessage id="error.contact_not_found" defaultMessage="Contact not found"/>,
    [errorCodes.no_contacts_found]:
        <FormattedMessage id="error.no_contacts_found" defaultMessage="No contacts were found"/>,
    [errorCodes.service_not_found]:
        <FormattedMessage id="error.service_not_found" defaultMessage="Unknown service"/>,
    [errorCodes.no_services_found]:
        <FormattedMessage id="error.services_not_found" defaultMessage="No services were found"/>,
    [errorCodes.unauthorized_access]:
        <FormattedMessage id="error.unauthorized" defaultMessage="Unauthorized access"/>,
    [errorCodes.create_ad_failure]:
        <FormattedMessage id="error.create_ad_failure" defaultMessage="Failed to create advert"/>,
    [errorCodes.update_ad_failure]:
        <FormattedMessage id="error.update_ad_failure" defaultMessage="Failed to update advert"/>,
    [errorCodes.service_no_public_number]:
        <FormattedMessage id="error.service_no_public_number" defaultMessage="Service does not have any public number"/>,
    [errorCodes.operator_module_unavailable]:
        '',
    [errorCodes.customer_module_unavailable]:
        '',
    [errorCodes.operator_not_approved]:
        <FormattedMessage id="error.operator_not_approved" defaultMessage="Operator account is not approved yet"/>,
    [errorCodes.text_message_too_long]:
        <FormattedMessage id="error.message_too_long" defaultMessage="Message is too long"/>,
    [errorCodes.customer_profile_not_found]:
        <FormattedMessage id="error.profile_not_found" defaultMessage="Customer profile was not found"/>,
    [errorCodes.customer_profile_note_not_found]:
        <FormattedMessage id="error.profile_note_not_found" defaultMessage="Customer profile note was not found"/>,
    [errorCodes.service_pricelist_not_found]:
        <FormattedMessage id="error.service_pricelist_not_found" defaultMessage="Service pricelist was not found"/>,
    [errorCodes.invalid_time_interval]:
        <FormattedMessage id="error.invalid_time_interval" defaultMessage="Invalid time interval"/>,
    [errorCodes.duplicate_time_interval]:
        <FormattedMessage id="error.duplicate_time_interval" defaultMessage="Duplicate time interval"/>,
    [errorCodes.customer_not_found]:
        <FormattedMessage id="error.customer_not_found" defaultMessage="Customer was not found"/>,
    [errorCodes.service_no_public_numbers_assigned]:
        <FormattedMessage id="error.no_numbers_assigned" defaultMessage="There are no public numbers assigned"/>,
    [errorCodes.invalid_value]:
        <FormattedMessage id="error.invalid_value" defaultMessage="Invalid value"/>,
    [errorCodes.operator_login_not_unique]:
        <FormattedMessage id="registration.error.already_registered" defaultMessage="User is already registered"/>,
    [errorCodes.operator_phone_number_not_unique]:
        <FormattedMessage id="registration.error.phone_number_used" defaultMessage="Phone number is already used"/>,
};

export function getStatusFromError(error) {
    debug(error);
    if (error.networkError) {
        return {
            type: 'danger',
            message: (
                <FormattedMessage id="error.network" defaultMessage="Could not connect to the server"/>)
        };
    }
    else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const err = error.graphQLErrors[0];
        let message;

        if (err.code) {
            message = errorMapping[err.code];
        }
        else {
            message = err.message;
        }

        return {
            message: message,
            type: 'danger'
        };
    }
    else {
        return {
            type: 'danger',
            message:
                <FormattedMessage id="error.generic.title" defaultMessage="Unexpected error occurred"/>
        };
    }
}

export function logGraphQLError(e) {
    if (Object.values(errorCodes).includes(e.code)) {
        return;
    }
    Raven.captureMessage(e.message, {
        extra: e,
        tags: {
            source: 'graphql',
        }
    });
}

export function logNetworkError(error) {
    const code = parseInt(error.statusCode, 10);
    switch (code) {
        case 400:
            Raven.captureMessage(error.message, {
                extra: error,
                tags: {
                    source: 'graphql',
                }
            });
            break;
        default:
            Raven.captureMessage(error.message, {
                extra: error,
                tags: {
                    source: 'graphql',
                }
            });
    }
}

export const graphQLErrorProcesor = (queries) => {
    if (!Array.isArray(queries)) {
        queries = [queries];
    }
    return (WrappedComponent) => {
        const GraphQLErrorProcessor = connect(
            state => ({}),
            dispatch => ({logout: () => dispatch(logout())})
        )(
            class extends React.Component {
                render() {
                    for (const name of queries) {
                        const query = this.props[name];
                        if (query && query.error) {
                            if (query.error.networkError) {
                                return <NetworkError/>;
                            }
                            for (const e of query.error.graphQLErrors) {
                                switch (e.code) {
                                    case errorCodes.invalid_access_token:
                                        this.props.logout();
                                        break;

                                    case errorCodes.no_assignable_public_number:
                                    case errorCodes.general_login_failure:
                                    case errorCodes.invalid_service_type:
                                    case errorCodes.owning_operator_not_specified:
                                    case errorCodes.authentication_module_unavailable:
                                    case errorCodes.operator_already_registered:
                                        break; // Specific codes that can be processed elsewhere

                                    case errorCodes.unauthorized_access:
                                        return <ForbiddenError/>;

                                    default:
                                        return <GenericError/>;
                                }
                            }
                        }
                    }

                    return <WrappedComponent {...this.props} />
                }
            }
        );

        GraphQLErrorProcessor.displayName = `GraphQLErrorProcessor(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
        return GraphQLErrorProcessor;
    }
};

export class ErrorHandler extends React.Component {
    componentWillMount() {
        this.setState({
            error: null,
        });
    }

    componentDidCatch(error, errorInfo) {
        this.setState({error});
        Raven.captureException(error, {extra: errorInfo});
    }

    render() {
        if (this.state.error) {
            return <GenericError/>;
        }

        return this.props.children;
    }
}


// WEBPACK FOOTER //
// ./src/helpers/errors.js